import { useState } from "react";
import { Form, Head, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import CaseWorkspace, {
  CaseFacts,
  CaseNavigation,
} from "@/components/case-workspace";
import { FormContent, FormFields, FormSection } from "@/components/page-canvas";
import {
  RecordLink,
  RecordMetadata,
  RecordTitle,
} from "@/components/record-link";
import { ConfirmSubmitButton } from "@/components/confirm-submit-button";
import { errorText, FormErrors } from "@/components/forms/form-errors";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  NativeSelect,
  NativeSelectOption,
} from "@/components/ui/native-select";
import SettingsLayout from "@/layouts/settings_layout";
import {
  userBillingPath,
  userInvitationsPath,
  userMemberActivationPath,
  userMemberPath,
  userTeamPath,
} from "@/routes";

type Location = { id: string; name: string };
type Member = {
  id: string;
  email_address: string;
  first_name: string;
  last_name: string;
  role: string;
  access_revision: string;
  locale: string;
  deactivated_at?: string | null;
  location_ids: string[];
  stored_location_ids: string[];
  historical_location_ids: string[];
  stored_historical_location_ids: string[];
  can_deactivate: boolean;
};
type Invitation = {
  id: string;
  email_address: string;
  role: string;
  locale: string;
  active: boolean;
  location_ids: string[];
};
type Section = "roster" | "invite" | "member";
type Props = {
  members: Member[];
  invitations: Invitation[];
  locations: Location[];
  retired_locations: Location[];
  can_invite: boolean;
  can_manage_roles: boolean;
  section: Section;
  selected_member_id: string | null;
};
const name = (member: Member) =>
  [member.first_name, member.last_name].filter(Boolean).join(" ") ||
  member.email_address;
function InlineError({ id, error }: { id: string; error: unknown }) {
  return errorText(error) ? (
    <p id={id} className="text-sm text-destructive">
      {errorText(error)}
    </p>
  ) : null;
}

function MemberEditor({
  member,
  locations,
  retiredLocations,
  canManageRoles,
  canInvite,
}: {
  member: Member;
  locations: Location[];
  retiredLocations: Location[];
  canManageRoles: boolean;
  canInvite: boolean;
}) {
  const { t } = useTranslation();
  const { errors } = usePage().props;
  const [selectedRole, setSelectedRole] = useState(member.role);
  const [selectedLocations, setSelectedLocations] = useState(
    member.role === "admin"
      ? locations.map((location) => location.id)
      : member.stored_location_ids,
  );
  const [selectedHistory, setSelectedHistory] = useState(
    member.stored_historical_location_ids,
  );
  const administrator = selectedRole === "admin";
  const toggle = (ids: string[], id: string, checked: boolean) =>
    checked
      ? [...ids.filter((value) => value !== id), id]
      : ids.filter((value) => value !== id);
  return (
    <FormContent>
      <header className="space-y-2">
        <h1 className="break-words text-xl font-semibold">{name(member)}</h1>
        <p className="break-all text-sm text-muted-foreground">
          {member.email_address}
        </p>
        <p className="text-sm text-muted-foreground">
          {t("access.member_impact")}
        </p>
      </header>
      <FormErrors
        errors={errors}
        targets={{ role: "member-role", locations: "member-locations" }}
      />
      {!canManageRoles && (
        <p role="status" className="text-sm text-muted-foreground">
          {t("product_ux_admin.team.roles_unavailable")}
        </p>
      )}
      <Form action={userMemberPath(member.id)} method="patch">
        {({ processing }) => (
          <div className="space-y-6">
            <input type="hidden" name="return_section" value="member" />
            <input type="hidden" name="user[combined_edit]" value="1" />
            <input
              type="hidden"
              name="user[access_revision]"
              value={member.access_revision}
            />
            <FormSection title={t("team.role")}>
              <div className="space-y-2">
                <Label htmlFor="member-role">{t("team.role")}</Label>
                <NativeSelect
                  id="member-role"
                  name="user[role]"
                  value={selectedRole}
                  onChange={(event) => setSelectedRole(event.target.value)}
                  disabled={!canManageRoles}
                  aria-invalid={!!errors.role}
                  aria-describedby={
                    errors.role ? "member-role-error" : undefined
                  }
                >
                  {["member", "manager", "admin"].map((role) => (
                    <NativeSelectOption key={role} value={role}>
                      {t(`team.${role}`)}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
                <InlineError id="member-role-error" error={errors.role} />
              </div>
            </FormSection>
            <FormSection
              title={t("team.active_access")}
              description={
                administrator ? t("team.admin_access_help") : undefined
              }
            >
              {!administrator && (
                <input type="hidden" name="user[location_ids][]" value="" />
              )}
              <fieldset
                id="member-locations"
                tabIndex={-1}
                className="space-y-3"
              >
                <legend className="mb-2 text-sm font-medium">
                  {t("common.locations")}
                </legend>
                {locations.map((item) => (
                  <label
                    key={item.id}
                    className="flex items-center gap-2 text-sm"
                  >
                    <Checkbox
                      name="user[location_ids][]"
                      value={item.id}
                      checked={
                        administrator || selectedLocations.includes(item.id)
                      }
                      onCheckedChange={(checked) =>
                        setSelectedLocations((ids) =>
                          toggle(ids, item.id, checked),
                        )
                      }
                      disabled={!canManageRoles || administrator}
                    />
                    {item.name}
                  </label>
                ))}
                <InlineError
                  id="member-locations-error"
                  error={errors.locations}
                />
              </fieldset>
            </FormSection>
            {administrator && retiredLocations.length > 0 && (
              <p className="text-sm text-muted-foreground">
                {t("team.admin_history_help")}
              </p>
            )}
            {member.stored_historical_location_ids.length > 0 && (
              <FormSection
                title={t("team.retired_history_access")}
                description={t("team.retired_history_help")}
              >
                {!administrator && (
                  <input
                    type="hidden"
                    name="user[historical_location_ids][]"
                    value=""
                  />
                )}
                {retiredLocations
                  .filter((item) =>
                    member.stored_historical_location_ids.includes(item.id),
                  )
                  .map((item) => (
                    <label
                      key={item.id}
                      className="flex items-center gap-2 text-sm"
                    >
                      <Checkbox
                        name="user[historical_location_ids][]"
                        value={item.id}
                        checked={selectedHistory.includes(item.id)}
                        onCheckedChange={(checked) =>
                          setSelectedHistory((ids) =>
                            toggle(ids, item.id, checked),
                          )
                        }
                        disabled={!canManageRoles || administrator}
                      />
                      {item.name}
                    </label>
                  ))}
              </FormSection>
            )}
            <div className="flex flex-wrap gap-3">
              <Button type="submit" disabled={!canManageRoles || processing}>
                {t("team.save_member")}
              </Button>
              <Button variant="outline" render={<Link href={userTeamPath()} />}>
                {t("common.cancel")}
              </Button>
            </div>
          </div>
        )}
      </Form>
      <FormSection
        className="border-t pt-6"
        title={t(member.deactivated_at ? "team.reactivate" : "team.deactivate")}
        description={t("product_ux_admin.team.activation_impact")}
      >
        <Form
          action={userMemberActivationPath(member.id)}
          method={member.deactivated_at ? "post" : "delete"}
        >
          {({ processing }) => (
            <>
              <input type="hidden" name="return_section" value="member" />
              {member.deactivated_at ? (
                <Button
                  type="submit"
                  variant="outline"
                  disabled={!canInvite || processing}
                >
                  {t("team.reactivate")}
                </Button>
              ) : (
                <ConfirmSubmitButton
                  title={t("team.deactivate_confirmation")}
                  description={t("team.deactivate_confirmation_help")}
                  confirmLabel={t("team.deactivate")}
                  cancelLabel={t("common.cancel")}
                  processing={processing}
                  disabled={!member.can_deactivate}
                >
                  {t("team.deactivate")}
                </ConfirmSubmitButton>
              )}
            </>
          )}
        </Form>
      </FormSection>
    </FormContent>
  );
}

export default function TeamSettings({
  members,
  invitations,
  locations,
  retired_locations: retiredLocations,
  can_invite: canInvite,
  can_manage_roles: canManageRoles,
  section,
  selected_member_id: selectedMemberId,
}: Props) {
  const { t } = useTranslation();
  const member = members.find((item) => item.id === selectedMemberId);
  const items = [
    {
      id: "roster",
      label: t("product_ux_admin.team.roster"),
      href: userTeamPath(),
    },
    {
      id: "invite",
      label: t("team.invite_title"),
      href: userTeamPath({ section: "invite" }),
    },
    ...(member
      ? [
          {
            id: "member",
            label: name(member),
            href: userTeamPath({ section: "member", member_id: member.id }),
          },
        ]
      : []),
  ];
  return (
    <SettingsLayout>
      <Head title={t("common.team")} />
      <CaseWorkspace
        fluid={section === "roster"}
        editor={section !== "roster"}
        context={
          <CaseFacts
            facts={[
              {
                label: t("common.team"),
                value: t("product_ux_admin.team.active_count", {
                  count: members.filter((item) => !item.deactivated_at).length,
                }),
              },
              {
                label: t("product_ux_admin.team.invitations"),
                value: t("product_ux_admin.team.pending_count", {
                  count: invitations.filter((item) => item.active).length,
                }),
              },
            ]}
          />
        }
        navigation={
          <CaseNavigation
            items={items}
            active={section}
            label={t("product_ux_admin.tasks")}
          />
        }
      >
        {section === "roster" && (
          <div className="space-y-6">
            <header className="space-y-2">
              <h1 className="text-xl font-semibold">{t("common.team")}</h1>
              <p className="text-sm text-muted-foreground">
                {t("product_ux_admin.team.roster_help")}
              </p>
            </header>
            <Button
              render={<Link href={userTeamPath({ section: "invite" })} />}
            >
              {t("team.invite_title")}
            </Button>
            <div className="divide-y rounded-lg border bg-card">
              {members.map((item) => (
                <article
                  key={item.id}
                  className="flex min-w-0 flex-wrap items-start justify-between gap-4 p-4"
                >
                  <div className="min-w-0 space-y-1">
                    <h2 className="break-words">
                      <RecordLink
                        href={userTeamPath({
                          section: "member",
                          member_id: item.id,
                        })}
                      >
                        <RecordTitle>{name(item)}</RecordTitle>
                      </RecordLink>
                    </h2>
                    <RecordMetadata className="break-all">
                      {item.email_address}
                    </RecordMetadata>
                    <Badge
                      variant={item.deactivated_at ? "outline" : "secondary"}
                    >
                      {item.deactivated_at
                        ? t("team.inactive")
                        : t(`team.${item.role}`)}
                    </Badge>
                  </div>
                  <Button
                    variant="outline"
                    render={
                      <Link
                        href={userTeamPath({
                          section: "member",
                          member_id: item.id,
                        })}
                      />
                    }
                  >
                    {t("access.edit_member")}
                  </Button>
                </article>
              ))}
            </div>
            {invitations.length > 0 && (
              <FormSection title={t("product_ux_admin.team.invitations")}>
                <div className="divide-y rounded-lg border bg-card">
                  {invitations.map((invitation) => (
                    <div
                      key={invitation.id}
                      className="flex min-w-0 flex-wrap justify-between gap-3 p-4"
                    >
                      <RecordTitle className="min-w-0 break-all no-underline">
                        {invitation.email_address}
                      </RecordTitle>
                      <div className="flex flex-wrap gap-2">
                        <Badge variant="outline">
                          {t(`team.${invitation.role}`)}
                        </Badge>
                        <Badge variant="outline">
                          {t(
                            invitation.active
                              ? "product_ux_admin.team.pending"
                              : "product_ux_admin.team.expired",
                          )}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </FormSection>
            )}
          </div>
        )}
        {section === "invite" && (
          <FormContent>
            <header className="space-y-2">
              <h1 className="text-xl font-semibold">
                {t("team.invite_title")}
              </h1>
              <p className="text-sm text-muted-foreground">
                {t("team.invite_description")}
              </p>
            </header>
            {!canInvite && (
              <div role="status" className="space-y-3 rounded-lg border p-4">
                <p>{t("access.team_limit")}</p>
                <Button
                  variant="outline"
                  render={<Link href={userBillingPath()} />}
                >
                  {t("product_ux_admin.review_subscription")}
                </Button>
              </div>
            )}
            <Form action={userInvitationsPath()} method="post" resetOnSuccess>
              {({ errors: formErrors, processing }) => (
                <div className="space-y-5">
                  <input type="hidden" name="return_section" value="invite" />
                  <FormErrors
                    errors={formErrors}
                    targets={{
                      email_address: "invite_email",
                      role: "invite_role",
                      locale: "invite_locale",
                      locations: "invite_locations",
                    }}
                  />
                  <div className="space-y-2">
                    <Label htmlFor="invite_email">{t("team.email")}</Label>
                    <Input
                      id="invite_email"
                      name="invitation[email_address]"
                      type="email"
                      required
                      aria-invalid={!!formErrors.email_address}
                      aria-describedby={
                        formErrors.email_address
                          ? "invite_email-error"
                          : undefined
                      }
                    />
                    <InlineError
                      id="invite_email-error"
                      error={formErrors.email_address}
                    />
                  </div>
                  <FormFields>
                    <div className="space-y-2">
                      <Label htmlFor="invite_role">{t("team.role")}</Label>
                      <NativeSelect
                        id="invite_role"
                        name="invitation[role]"
                        defaultValue="member"
                        aria-invalid={!!formErrors.role}
                      >
                        <NativeSelectOption value="member">
                          {t("team.member")}
                        </NativeSelectOption>
                        <NativeSelectOption value="manager">
                          {t("team.manager")}
                        </NativeSelectOption>
                        <NativeSelectOption value="admin">
                          {t("team.admin")}
                        </NativeSelectOption>
                      </NativeSelect>
                      <InlineError
                        id="invite_role-error"
                        error={formErrors.role}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="invite_locale">
                        {t("common.language")}
                      </Label>
                      <NativeSelect
                        id="invite_locale"
                        name="invitation[locale]"
                        defaultValue="en"
                      >
                        <NativeSelectOption value="en">
                          English
                        </NativeSelectOption>
                        <NativeSelectOption value="ro">
                          Română
                        </NativeSelectOption>
                      </NativeSelect>
                    </div>
                  </FormFields>
                  <fieldset
                    id="invite_locations"
                    tabIndex={-1}
                    className="space-y-3"
                  >
                    <legend className="mb-2 text-sm font-medium">
                      {t("common.locations")}
                    </legend>
                    {locations.map((item) => (
                      <label
                        key={item.id}
                        className="flex items-center gap-2 text-sm"
                      >
                        <Checkbox
                          name="invitation[location_ids][]"
                          value={item.id}
                          defaultChecked
                        />
                        {item.name}
                      </label>
                    ))}
                    <InlineError
                      id="invite_locations-error"
                      error={formErrors.locations}
                    />
                  </fieldset>
                  <p className="text-sm text-muted-foreground">
                    {t("product_ux_admin.team.invite_impact")}
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Button type="submit" disabled={!canInvite || processing}>
                      {t("team.send_invitation")}
                    </Button>
                    <Button
                      variant="outline"
                      render={<Link href={userTeamPath()} />}
                    >
                      {t("common.cancel")}
                    </Button>
                  </div>
                </div>
              )}
            </Form>
          </FormContent>
        )}
        {section === "member" && member && (
          <MemberEditor
            key={[
              member.id,
              member.role,
              member.access_revision,
              ...member.location_ids.slice().sort(),
              ...member.stored_location_ids.slice().sort(),
              ...member.stored_historical_location_ids.slice().sort(),
            ].join(":")}
            member={member}
            locations={locations}
            retiredLocations={retiredLocations}
            canManageRoles={canManageRoles}
            canInvite={canInvite}
          />
        )}
      </CaseWorkspace>
    </SettingsLayout>
  );
}
